import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PopupService } from 'shared/popup-service/popup.service';

@Injectable({
    providedIn: 'root',
})
export class DebugModeService {
    private _showComponentNames = new BehaviorSubject<boolean>(false);
    public showComponentNames$ = this._showComponentNames.asObservable();
    private labelElements: HTMLElement[] = [];

    constructor(private popup: PopupService) {
        this.initKeyboardListener();
        this.subscribeToChanges();
    }

    private initKeyboardListener(): void {
        document.addEventListener('keydown', (event) => {
            // Check for CTRL+SHIFT+W
            if (event.ctrlKey && event.shiftKey && event.key === 'W') {
                event.preventDefault();
                this.toggleComponentNames();
            }
        });
    }

    private subscribeToChanges(): void {
        this.showComponentNames$.subscribe((show) => {
            if (show) {
                this.showAllComponents();
            } else {
                this.hideAllComponents();
            }
        });
    }

    public toggleComponentNames(): void {
        const currentValue = this._showComponentNames.value;
        this._showComponentNames.next(!currentValue);

        // Show toast notification
        this.popup.neutral(`Component debug mode: ${!currentValue ? 'ON' : 'OFF'}`);
    }

    private showAllComponents(): void {
        // Clear any existing labels first
        this.hideAllComponents();

        // Find all elements and add hover listeners instead of permanent labels
        const allElements = document.querySelectorAll('*');
        // Setting up hover listeners for elements

        // componentCount removed - not used
        allElements.forEach((element) => {
            const componentName = this.getAngularComponentName(element);
            if (componentName) {
                this.addHoverListeners(element as HTMLElement, componentName);
                // component found
            }
        });

        // Hover listeners added to Angular components

        // Set up a mutation observer to handle dynamically added components
        this.setupMutationObserver();
    }
    private getAngularComponentName(element: Element): string | null {
        const tagName = element.tagName.toLowerCase();

        // Skip certain elements that are definitely not components
        if (['html', 'head', 'body', 'script', 'style', 'meta', 'title', 'link', 'br', 'hr', 'img'].includes(tagName)) {
            return null;
        }

        // Check if it's a custom Angular component (ce-, app-, mat-, ng- prefixes)
        if (tagName.includes('-')) {
            // Found component
            return tagName; // Just return the tag name as-is
        }

        // Check for Angular attributes to catch components without dashes
        const attributes = element.getAttributeNames();
        const hasNgHost = attributes.some((attr) => attr.startsWith('_nghost-'));

        if (hasNgHost) {
            // Found nghost component
            return tagName; // Return tag name for any element with nghost
        }

        return null;
    }

    private getComponentFromAttributes(_element: Element, _attributes: string[]): string | null {
        // This method is no longer needed since we're just returning tag names
        return null;
    }

    private isAngularCustomComponent(tagName: string): boolean {
        // Check for common Angular component prefixes
        const componentPrefixes = ['ce-', 'app-'];
        return componentPrefixes.some((prefix) => tagName.startsWith(prefix));
    }

    private isKnownAngularComponent(tagName: string): boolean {
        // Angular Material components
        if (tagName.startsWith('mat-')) return true;

        // Angular built-in components
        if (tagName.startsWith('ng-')) return true;

        // Angular CDK components
        if (tagName.startsWith('cdk-')) return true;

        // Angular forms
        if (['form', 'input', 'select', 'textarea'].includes(tagName)) {
            return false; // These are native HTML, not Angular components
        }

        return false;
    }

    private tagNameToComponentName(tagName: string): string {
        // Convert tag name to likely component class name
        // ce-topbar -> TopbarComponent
        // mat-button -> MatButton (Material components don't always end with Component)
        // app-header -> AppHeaderComponent

        // Special handling for known elements
        if (tagName.includes('-component-')) {
            // Already processed nghost attribute case
            return tagName;
        }

        let componentName = tagName
            .split('-')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join('');

        // For Material components, they might not follow the *Component pattern
        if (tagName.startsWith('mat-')) {
            return componentName; // MatButton, MatFormField, etc.
        }

        // For Angular built-ins
        if (tagName.startsWith('ng-')) {
            return componentName; // NgFor, NgIf, etc.
        }

        // For custom components, add Component suffix if not already there
        if (!componentName.endsWith('Component')) {
            componentName += 'Component';
        }

        return componentName;
    }

    private addHoverListeners(element: HTMLElement, componentName: string): void {
        // Store the component name as a data attribute
        element.setAttribute('data-debug-component', componentName);

        // Add hover event listeners
        const mouseEnterHandler = (_event: MouseEvent) => {
            if (this._showComponentNames.value) {
                this.showHoverLabel(element, componentName);
            }
        };

        const mouseLeaveHandler = (_event: MouseEvent) => {
            this.hideHoverLabel(element);
        };

        element.addEventListener('mouseenter', mouseEnterHandler);
        element.addEventListener('mouseleave', mouseLeaveHandler);

        // Store handlers for cleanup later
        (element as any).__debugMouseEnter = mouseEnterHandler;
        (element as any).__debugMouseLeave = mouseLeaveHandler;
    }

    private showHoverLabel(element: HTMLElement, componentName: string): void {
        // Remove any existing hover label first
        this.hideHoverLabel(element);

        const label = document.createElement('div');
        label.className = 'debug-component-hover-label';
        label.innerHTML = `&lt;${componentName}&gt;`;

        // Get element position
        const rect = element.getBoundingClientRect();

        // Find labels that would overlap with this position to determine stack offset
        const existingLabels = Array.from(document.querySelectorAll('.debug-component-hover-label'));
        const overlappingLabels = existingLabels.filter((existingLabel) => {
            const existingRect = existingLabel.getBoundingClientRect();
            const horizontalOverlap = Math.abs(existingRect.left - rect.left) < 200; // Within 200px horizontally
            const verticalOverlap = Math.abs(existingRect.top - (rect.top - 25)) < 30; // Within 30px vertically
            return horizontalOverlap && verticalOverlap;
        });

        const stackOffset = overlappingLabels.length * 22; // 22px per overlapping label

        // Style the hover label with proper stacking
        Object.assign(label.style, {
            position: 'fixed',
            top: `${Math.max(10, rect.top - 25 - stackOffset)}px`, // Stack upwards from component
            left: `${rect.left}px`,
            background: 'rgba(138, 43, 226, 0.95)',
            color: 'white',
            padding: '3px 8px',
            fontSize: '11px',
            fontFamily: 'Monaco, "Lucida Console", monospace',
            zIndex: `${10001 + overlappingLabels.length}`, // Higher z-index for overlapping labels
            borderRadius: '4px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '2px 2px 8px rgba(0,0,0,0.6)',
            lineHeight: '1.2',
            fontWeight: '600',
            border: '1px solid rgba(255,255,255,0.6)',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            animation: 'fadeInStack 0.2s ease-in',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        });

        // Add stacking animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeInStack {
                from {
                    opacity: 0;
                    transform: translateY(5px) scale(0.9);
                }
                to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                }
            }
        `;
        if (!document.querySelector('style[data-debug-stack-animation]')) {
            style.setAttribute('data-debug-stack-animation', 'true');
            document.head.appendChild(style);
        }

        document.body.appendChild(label);

        // Store reference for cleanup
        (element as any).__debugHoverLabel = label;
    }

    private hideHoverLabel(element: HTMLElement): void {
        const existingLabel = (element as any).__debugHoverLabel;
        if (existingLabel && existingLabel.parentNode) {
            existingLabel.parentNode.removeChild(existingLabel);
            (element as any).__debugHoverLabel = null;
        }
    }

    private addComponentLabel(element: HTMLElement, componentName: string): void {
        // Skip if element already has a label
        if (element.querySelector('.debug-component-label')) {
            return;
        }

        const label = document.createElement('div');
        label.className = 'debug-component-label';
        label.innerHTML = `&lt;${componentName}&gt;`; // Show as <ce-tile-basic>

        // Check element size to determine best label position
        const rect = element.getBoundingClientRect();
        const isSmallElement = rect.width < 80 || rect.height < 30;

        // Style the label to look like CSS/HTML tags
        Object.assign(label.style, {
            position: isSmallElement ? 'fixed' : 'absolute',
            top: isSmallElement ? `${rect.top - 20}px` : '0',
            left: isSmallElement ? `${rect.left}px` : '0',
            background: 'rgba(138, 43, 226, 0.95)', // Brighter purple for better visibility
            color: 'white',
            padding: '2px 6px',
            fontSize: '10px',
            fontFamily: 'Monaco, "Lucida Console", monospace',
            zIndex: '10000',
            borderRadius: '3px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '2px 2px 8px rgba(0,0,0,0.6)',
            lineHeight: '1.2',
            fontWeight: '600',
            border: '1px solid rgba(255,255,255,0.6)',
            minWidth: isSmallElement ? 'auto' : '60px',
            textAlign: 'center',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            transform: isSmallElement ? 'translateY(-100%)' : 'none',
        });

        // For small elements, position above the element to avoid clipping
        if (isSmallElement) {
            // Use fixed positioning relative to viewport
            document.body.appendChild(label);
        } else {
            // Make the element position relative if needed
            const position = window.getComputedStyle(element).position;
            if (position === 'static') {
                element.style.position = 'relative';
                element.style.overflow = 'visible';
            }
            element.appendChild(label);
        }

        this.labelElements.push(label);
    }

    private setupMutationObserver(): void {
        const observer = new MutationObserver((mutations) => {
            if (!this._showComponentNames.value) return;

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;
                        const componentName = this.getAngularComponentName(element);

                        if (componentName) {
                            this.addHoverListeners(element, componentName);
                        }

                        // Also check child elements
                        const childElements = element.querySelectorAll('*');
                        childElements.forEach((child) => {
                            const childComponentName = this.getAngularComponentName(child);
                            if (childComponentName) {
                                this.addHoverListeners(child as HTMLElement, childComponentName);
                            }
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Store observer reference for cleanup
        (this as any).mutationObserver = observer;
    }

    private hideAllComponents(): void {
        // Remove all existing permanent labels
        this.labelElements.forEach((label) => {
            if (label.parentNode) {
                label.parentNode.removeChild(label);
            }
        });
        this.labelElements = [];

        // Remove all permanent labels that might have been added elsewhere
        const allLabels = document.querySelectorAll('.debug-component-label');
        allLabels.forEach((label) => {
            if (label.parentNode) {
                label.parentNode.removeChild(label);
            }
        });

        // Remove hover listeners and hover labels
        const elementsWithListeners = document.querySelectorAll('[data-debug-component]');
        elementsWithListeners.forEach((element) => {
            const htmlElement = element as HTMLElement;

            // Remove hover label if exists
            this.hideHoverLabel(htmlElement);

            // Remove event listeners
            const mouseEnterHandler = (htmlElement as any).__debugMouseEnter;
            const mouseLeaveHandler = (htmlElement as any).__debugMouseLeave;

            if (mouseEnterHandler) {
                htmlElement.removeEventListener('mouseenter', mouseEnterHandler);
                delete (htmlElement as any).__debugMouseEnter;
            }

            if (mouseLeaveHandler) {
                htmlElement.removeEventListener('mouseleave', mouseLeaveHandler);
                delete (htmlElement as any).__debugMouseLeave;
            }

            // Remove data attribute
            htmlElement.removeAttribute('data-debug-component');
        });

        // Remove any remaining hover labels
        const hoverLabels = document.querySelectorAll('.debug-component-hover-label');
        hoverLabels.forEach((label) => {
            if (label.parentNode) {
                label.parentNode.removeChild(label);
            }
        });

        // Disconnect mutation observer
        if ((this as any).mutationObserver) {
            (this as any).mutationObserver.disconnect();
            delete (this as any).mutationObserver;
        }
    }

    public get isEnabled(): boolean {
        return this._showComponentNames.value;
    }
}
